/**
 * Datáfono — anotar en segundos lo que se cobra por el datáfono (2026-08-19,
 * Dirección: «que administración lo tenga disponible para compartir con la gestoría»).
 *
 * No inventa nada: cada apunte es un COBRO normal (POST /api/cobros) con
 * método «Tarjeta», así que sale en Cobros, en el saldo del paciente y en los
 * Excel de siempre. Los botones de concepto vienen de los cobros reales con
 * tarjeta, por frecuencia — si la clínica empieza a cobrar otra cosa, el botón
 * aparece solo.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CreditCard, Loader2, Lock, Save, TriangleAlert } from 'lucide-react';
import { api, formatDate } from '../lib/api';
import { useAuth } from '../lib/auth';
import { isManagement } from '../lib/roles';
import { PageHeader } from '../components/Common';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { PatientCombobox } from '../components/PatientCombobox';
import { avisoDeLoQueFalta } from '../lib/cobroQuickHelpers';
import { suggestedAmountForConcept } from '../lib/cobroPresets';
import { PresupuestoDelCobroField, usePresupuestoDelCobro } from '../components/cobros/PresupuestoDelCobro';
import { ReservaDeQueCitaField, useReservasPendientes } from '../components/cobros/ReservaDeQueCita';

export default function DatafonoPage() {
  const { user } = useAuth();
  // ANOTAR lo puede hacer el equipo salvo los perfiles clínicos (POST
  // /api/cobros, cerrado el 2026-09-17). Del LISTADO, dirección lo ve entero y
  // el mostrador solo un DÍA: GET /api/cobros pide `date` a quien no es
  // dirección. Aquí se enseña la lista solo a dirección para no pedir de más;
  // si algún día se quiere que el mostrador vea sus pagos con tarjeta de hoy,
  // ya se puede — basta pedir `?date=` en vez de nada.
  const puedeVerListado = isManagement(user);
  const [conceptos, setConceptos] = useState([]);
  const [paciente, setPaciente] = useState(null);
  const [concepto, setConcepto] = useState('');
  const [importe, setImporte] = useState('');
  // La fecha se elige siempre (clínica, 30/9/2026: «ni la fecha… debe
  // aparecer prerrellenada»). Hasta ese día no había campo: el servidor le
  // ponía la de hoy, y un pago del viernes anotado el lunes salía del lunes.
  const [fecha, setFecha] = useState('');
  const [guardando, setGuardando] = useState(false);
  // El presupuesto del que cuelga el pago (12/10/2026), como en Cobros.
  const presupuesto = usePresupuestoDelCobro(paciente?.id || '', concepto);
  // «¿Este pago es de alguna de estas citas con reserva?» (12/10/2026;
  // 15/10/2026), como en Cobros: con una o varias citas con la reserva sin
  // pagar que paga este concepto, hay que contestar.
  const reserva = useReservasPendientes(paciente?.id || '', concepto);
  const [recientes, setRecientes] = useState(null);
  // Por qué un estado aparte y no una lista vacía: aquí se habla de DINERO.
  // Una lista vacía dice «no hay ningún cobro con tarjeta», y eso hace que
  // alguien vuelva a apuntar un cobro que ya estaba guardado. Un fallo tiene
  // que verse como fallo. Aquí NO se esconde la tarjeta entera —se elige
  // decirlo— porque la persona necesita saber que sus apuntes sí se guardan,
  // aunque no pueda repasarlos desde esta pantalla.
  const [falloRecientes, setFalloRecientes] = useState(null); // null | 'permiso' | 'error'

  const cargarRecientes = useCallback(() => {
    // Los últimos cobros con tarjeta: lo que administración repasa y comparte.
    if (!puedeVerListado) {
      // No se pide al servidor lo que ya se sabe que va a negar: se dice.
      setRecientes([]);
      setFalloRecientes('permiso');
      return;
    }
    setFalloRecientes(null);
    api.get('/api/cobros', { params: { q: '' } })
      .then((r) => {
        const items = (Array.isArray(r.data) ? r.data : [])
          .filter((c) => /tarjeta/i.test(c.payment_method || ''))
          .slice(0, 25);
        setRecientes(items);
        setFalloRecientes(null);
      })
      .catch((e) => {
        // El servidor manda: si nos dice 403 aunque el rol pareciera bastar,
        // se cuenta como falta de permiso; cualquier otro fallo, como fallo.
        setRecientes([]);
        setFalloRecientes(e?.response?.status === 403 ? 'permiso' : 'error');
      });
  }, [puedeVerListado]);

  useEffect(() => {
    api.get('/api/cobros/conceptos-tarjeta')
      .then((r) => setConceptos(Array.isArray(r.data) ? r.data : []))
      .catch(() => setConceptos([]));
    cargarRecientes();
  }, [cargarRecientes]);

  // Pulsar un concepto con precio fijo rellena el importe, como elegirlo en
  // la lista del resto de cobros (lib/cobroPresets). Uno sin precio deja el
  // importe como estaba; escribir a mano no toca el importe.
  const elegirConcepto = (valor) => {
    setConcepto(valor);
    const sugerido = suggestedAmountForConcept(valor);
    if (sugerido) setImporte(sugerido);
  };

  const guardar = async () => {
    const imp = parseFloat(String(importe).replace(',', '.'));
    // Todo lo que falta, en un solo aviso (clínica, 30/9/2026).
    const aviso = avisoDeLoQueFalta([
      !paciente?.id && 'el paciente',
      !fecha && 'la fecha',
      !(imp > 0) && 'el importe cobrado',
      !concepto.trim() && 'el concepto (o pulsa uno de los botones)',
      reserva.falta && 'si el pago es de alguna cita con reserva',
    ]);
    if (aviso) { toast.error(aviso); return; }
    setGuardando(true);
    try {
      await api.post('/api/cobros', {
        patient_id: paciente.id,
        patient_name: `${paciente.name || ''} ${paciente.surname1 || ''} ${paciente.surname2 || ''}`.replace(/\s+/g, ' ').trim(),
        nif: paciente.nif || '',
        concept: concepto.trim(),
        amount: imp,
        // El método ES esta pantalla: aquí solo se anotan pagos con tarjeta.
        payment_method: 'Tarjeta',
        date: fecha,
        ...(presupuesto.budgetId ? { budget_id: presupuesto.budgetId } : {}),
        ...(reserva.reservaCitaId ? { reserva_cita_id: reserva.reservaCitaId } : {}),
      });
      toast.success(`Anotado: ${concepto.trim()} · ${imp.toFixed(2)} € con tarjeta`);
      setConcepto('');
      setImporte('');
      setFecha('');
      setPaciente(null);
      cargarRecientes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo anotar el cobro');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Datáfono"
        subtitle="Anota aquí cada pago con tarjeta. Queda como un cobro normal: sale en Cobros, en el saldo del paciente y en los Excel."
      />

      <Card className="cjo-card p-4 space-y-4" data-testid="datafono-form">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <Label>Paciente</Label>
            <div className="mt-1.5">
              <PatientCombobox
                value={paciente?.id || null}
                onChange={(id, p) => {
                  if (p) { setPaciente(p); return; }
                  // El buscador del servidor puede devolver solo el id.
                  if (id) api.get(`/api/patients/${id}`).then((r) => setPaciente(r.data)).catch(() => setPaciente(null));
                  else setPaciente(null);
                }}
                dataTestid="datafono-paciente"
              />
            </div>
          </div>
          <div>
            <Label>Fecha</Label>
            <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)}
              className="mt-1.5" data-testid="datafono-fecha" />
          </div>
          <div>
            <Label>Importe cobrado (€)</Label>
            <Input value={importe} onChange={(e) => setImporte(e.target.value)}
              inputMode="decimal" placeholder="0,00" className="mt-1.5 text-lg font-semibold tabular-nums"
              data-testid="datafono-importe" />
          </div>
        </div>

        <div>
          <Label>Concepto</Label>
          {conceptos.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-1.5 mb-2">
              {conceptos.map((c) => (
                <button key={c} type="button" onClick={() => elegirConcepto(c)}
                  className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${
                    concepto === c ? 'border-[#2E6E5E] bg-[rgba(46,110,94,0.08)] font-semibold' : 'border-[#D9E3DD] hover:border-[#2E6E5E]'
                  }`}
                  data-testid={`datafono-concepto-${c.toLowerCase().replace(/\s+/g, '-')}`}>
                  {c}
                </button>
              ))}
            </div>
          )}
          <Input value={concepto} onChange={(e) => setConcepto(e.target.value)}
            placeholder="O escríbelo: Reserva, Consulta previa…" data-testid="datafono-concepto-libre" />
        </div>

        <PresupuestoDelCobroField estado={presupuesto} dataTestid="datafono-presupuesto" className="md:max-w-md" />
        <ReservaDeQueCitaField estado={reserva} prefijo="datafono-reserva" className="md:max-w-md" />

        <div className="flex justify-end">
          <Button onClick={guardar} disabled={guardando}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="datafono-guardar">
            {guardando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Anotar cobro con tarjeta
          </Button>
        </div>
      </Card>

      <Card className="cjo-card p-4">
        <div className="flex items-center gap-2 mb-3">
          <CreditCard className="w-4 h-4 text-[#2E6E5E]" />
          <h3 className="font-display font-semibold text-[#13231E]">Últimos pagos con tarjeta</h3>
        </div>
        {falloRecientes === 'permiso' ? (
          // Se dice en claro: no es que no haya cobros, es que no se pueden ver.
          <div className="flex items-start gap-2 rounded-md border border-[#E4D9B8] bg-[#FBF7E9] p-3 text-sm text-[#5B4B12]"
            data-testid="datafono-recientes-sin-permiso">
            <Lock className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              <strong>No puedes ver el listado de cobros</strong> (es de dirección), así que aquí no
              se enseña ninguno. <strong>No significa que no haya:</strong> lo que anotes arriba se
              guarda igual. Si necesitas repasarlos, pídeselo a dirección.
            </span>
          </div>
        ) : falloRecientes === 'error' ? (
          <div className="flex items-start gap-2 rounded-md border border-[#F0C9C4] bg-[#FDF2F1] p-3 text-sm text-[#8A2B20]"
            data-testid="datafono-recientes-error">
            <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              No se ha podido cargar el listado. <strong>No es que no haya cobros:</strong> vuelve a
              entrar en un momento antes de dar ninguno por perdido.
            </span>
          </div>
        ) : recientes === null ? (
          <div className="py-4 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Cargando…</div>
        ) : recientes.length === 0 ? (
          <p className="text-sm text-muted-foreground">Todavía no hay pagos con tarjeta anotados.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[480px]">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-muted-foreground text-left">
                  <th className="py-1.5 pr-3">Fecha</th>
                  <th className="py-1.5 pr-3">Paciente</th>
                  <th className="py-1.5 pr-3">Concepto</th>
                  <th className="py-1.5 text-right">Importe</th>
                </tr>
              </thead>
              <tbody>
                {recientes.map((c) => (
                  <tr key={c.id} className="border-t border-[#EEF3F0]">
                    <td className="py-1.5 pr-3 whitespace-nowrap tabular-nums">{formatDate(c.date)}</td>
                    <td className="py-1.5 pr-3">{c.patient_name}</td>
                    <td className="py-1.5 pr-3">{c.concept}</td>
                    <td className="py-1.5 text-right font-semibold tabular-nums">
                      {Number(c.total ?? c.amount ?? 0).toFixed(2)} €
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* El Excel de Cobros también es de dirección: no se manda a nadie a
            una puerta cerrada. */}
        <p className="mt-3 text-xs text-muted-foreground">
          {puedeVerListado ? (
            <>
              Para compartirlo (p. ej. con la gestoría): en <strong>Cobros</strong> está el Excel del mes
              con todos los pagos y su método; estos salen como «Tarjeta».
            </>
          ) : (
            <>
              Para compartirlo (p. ej. con la gestoría), el Excel del mes lo saca <strong>dirección</strong> desde
              Cobros; estos pagos salen ahí como «Tarjeta».
            </>
          )}
        </p>
      </Card>
    </div>
  );
}
